import { BackendError } from '@oresundsbron/api';
import { Box, Grid, Typography, Spinner } from '@oresundsbron/bridge-ui';
import { FormStoreProvider } from '@oresundsbron/use-form';
import {
  PrivateITicketOrderInputV2,
  PrivateITicketOrderResponse,
} from '@oresundsbron/validators';
import { pipe } from 'fp-ts/lib/function';
import { alt, fromNullable, getOrElse, map } from 'fp-ts/lib/Option';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { ParsedUrlQuery } from 'querystring';
import { ReactNode, useEffect, useState } from 'react';
import { CreditCardPayment } from './CreditCardPayment';
import { Error } from './Error';
import { OnlineTicketOrder } from './Order';
import { Refund } from './Refund';
import { Result } from './Result';
import {
  resetStore,
  usePrivateITicketData,
} from './stores/usePrivateITicketData';

const renderError = (error?: BackendError) =>
  pipe(
    error,
    fromNullable,
    map(() => <Error />)
  );

const renderCreditCardPayment = (data?: PrivateITicketOrderInputV2) =>
  pipe(
    data,
    fromNullable,
    map(() => <CreditCardPayment />)
  );

const renderResult = (resp?: PrivateITicketOrderResponse) =>
  pipe(
    resp,
    fromNullable,
    map(() => <Result />)
  );

const renderRefund = (query: ParsedUrlQuery) =>
  pipe(
    query.refund,
    fromNullable,
    map(() => <Refund />)
  );

export const OnlineTicket = () => {
  const { t } = useTranslation(['onlineTicket', 'common'], {
    nsMode: 'fallback',
  });

  const [content, setContent] = useState<ReactNode>(() => (
    <Box className="item-center col-span-full flex h-[600px] items-center justify-center text-5xl text-primary-600 md:col-span-6 md:col-start-4">
      <Spinner label={t('action.loading')} />
    </Box>
  ));
  const { response, error, data } = usePrivateITicketData();
  const { query, isReady } = useRouter();

  useEffect(() => {
    if (!isReady) {
      return;
    }
    const cont = pipe(
      error,
      renderError,
      alt(() => pipe(response, renderResult)),
      alt(() => pipe(data, renderCreditCardPayment)),
      alt(() => pipe(query, renderRefund)),
      getOrElse(() => <OnlineTicketOrder />)
    );
    setContent(cont);
  }, [error, data, response, query, isReady]);

  useEffect(
    () => () => {
      resetStore();
    },
    []
  );

  return (
    <FormStoreProvider>
      <Grid className="col-start-2">{content}</Grid>
      <Script
        strategy="lazyOnload"
        src="https://checkout.reepay.com/checkout.js"
      />
    </FormStoreProvider>
  );
};
